import { fetchBackgroundData } from '../../redux/api/portalApi';
import { fetchAllGeneralProjectPages } from '../../redux/api/generalProjectPagination';
import useGuardedRealtimeList, { readListRows } from '../../realtime/useGuardedRealtimeList';
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { formActivity } from '../../realtime/formActivity';
import { useDispatch, useSelector } from "react-redux";
import { Table, Spinner, Alert, Badge, Accordion, Form } from "react-bootstrap";
import { selectUserDetails, selectMappedRoles } from "../../redux/slices/userSlice";
import { setJobId } from "../../redux/slices/jobIdSlice";
import { useNavigate } from "react-router-dom";
import { patchData } from '../../redux/slices/apiCallSlice';
import { portalApi } from '../../redux/api/portalApi';
// Import centralized date utilities
import { formatDateLocalEnhanced } from '../../utils/dateUtils';
import { getProjectId, idsMatch } from '../../utils/dataNormalization';
import EngineeringPortalNoticeBanner from '../common/EngineeringPortalNoticeBanner';
import { usePermissions } from "../../hooks/usePermissions";

function OpenChangeOrderTasks() {
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.name;
  const rawMappedRoles = useSelector(selectMappedRoles);
  const mappedRoles = useMemo(() => rawMappedRoles || {}, [rawMappedRoles]);
  const { isAdmin } = usePermissions();
  const isAdminUser = isAdmin();
  const roles = useMemo(() => ({
    ...mappedRoles,
    Admin: isAdminUser,
  }), [mappedRoles, isAdminUser]);
  const dispatch = useDispatch();
  const generalProjectUrl = useSelector(state => state.apiUrls.urls.generalProject);
  const navigate = useNavigate();  
  
  // Component-level state for managing all data
  const [jobsData, setJobsData] = useState([]);
  const [changeOrderDetailsData, setChangeOrderDetailsData] = useState([]);
  const [changeOrderParentData, setChangeOrderParentData] = useState([]);
  const [generalProjectData, setGeneralProjectData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [groupedTasks, setGroupedTasks] = useState({});
  const [unassignedByDept, setUnassignedByDept] = useState({});
  const [accordionSelection, setAccordionSelection] = useState(null);
  const pendingTaskIds = useRef(new Set());
  const [savingTaskIds, setSavingTaskIds] = useState(new Set());
  const currentUserKey = Object.keys(groupedTasks).find(name =>
    name.trim().toLowerCase() === currentUser?.trim().toLowerCase());
  const activeAccordionKey = accordionSelection && accordionSelection.user === currentUser
    ? accordionSelection.key
    : (currentUserKey ? `user:${currentUserKey}` : null);

  useGuardedRealtimeList({ apiKey: 'generalProject', blocked: loading,
    load: async ({ signal }) => (await fetchAllGeneralProjectPages(async page => {
      const data = await dispatch(fetchBackgroundData({ url: generalProjectUrl, signal, params: { page } }));
      readListRows(data);
      if (Number(data?.pagination?.totalPages) > 1000) throw new Error('INVALID_PAGE_COUNT');
      return { data };
    })).records,
    apply: rows => { setJobsData(rows); setGeneralProjectData(rows); } });
  useGuardedRealtimeList({ apiKey: 'changeOrderDetails', blocked: loading, apply: setChangeOrderDetailsData });
  useGuardedRealtimeList({ apiKey: 'changeOrderParent', blocked: loading, apply: setChangeOrderParentData });

  // Fetch all required data when component mounts
  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all required data in parallel
      const [jobsResponse, changeOrderDetailsResponse, changeOrderParentResponse] = await Promise.allSettled([
        dispatch(portalApi.endpoints.getJobList.initiate(undefined, { forceRefetch: true, subscribe: false })).unwrap(),
        dispatch(portalApi.endpoints.getChangeOrderDetails.initiate(undefined, { forceRefetch: true, subscribe: false })).unwrap(),
        dispatch(portalApi.endpoints.getChangeOrderParent.initiate(undefined, { forceRefetch: true, subscribe: false })).unwrap(),
      ]);

      // Extract data from fulfilled promises
      if (jobsResponse.status === 'fulfilled') {
        const normalizedJobs = Array.isArray(jobsResponse.value) ? jobsResponse.value : [];
        setJobsData(normalizedJobs);
        setGeneralProjectData(normalizedJobs);
      } else {
        console.error('Failed to fetch jobs:', jobsResponse.reason);
      }

      if (changeOrderDetailsResponse.status === 'fulfilled') {
        setChangeOrderDetailsData(Array.isArray(changeOrderDetailsResponse.value) ? changeOrderDetailsResponse.value : []);
      } else {
        console.error('Failed to fetch change order details:', changeOrderDetailsResponse.reason);
      }

      if (changeOrderParentResponse.status === 'fulfilled') {
        setChangeOrderParentData(Array.isArray(changeOrderParentResponse.value) ? changeOrderParentResponse.value : []);
      } else {
        console.error('Failed to fetch change order parent:', changeOrderParentResponse.reason);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load change order tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Determine if user is in a specific department
  const userIsMechanical = useCallback(() => roles.MechanicalEngineer, [roles]);
  const userIsElectrical = useCallback(() => roles.ElectricalEngineer, [roles]);
  const userIsControls = useCallback(() => roles.ControlEngineer, [roles]);

  // Function to check if the current user has permission to modify a specific task
  const canModifyTask = (task) => {
    if (savingTaskIds.has(String(task.ID))) return false;
    // Admin users can always modify
    if (roles.Admin) return true;
    
    // Check if the current user is the assigned person for this task
    return task.assignedTo === currentUser;
  };  // Function to handle checkbox changes for acknowledgment and completion
  const handleCheckboxChange = async (task, field, value) => {
    const taskId = String(task.ID);
    if (!canModifyTask(task) || !field || pendingTaskIds.current.has(taskId)) return;
    pendingTaskIds.current.add(taskId);
    setSavingTaskIds(new Set(pendingTaskIds.current));
    setError(null);
    const finishSaving = formActivity.saving();
    
    const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
    const data = { [field]: String(value), ID: task.ID };

    try {
      // Dispatch the patch action and wait for completion
      await dispatch(patchData({ url, data })).unwrap();
      
      // Keep the list mounted and update only the successfully saved record.
      setChangeOrderDetailsData(previous => previous.map(detail =>
        String(detail.ID) === taskId ? { ...detail, [field]: value } : detail));
      
    } catch (error) {
      console.error("Error updating task:", error);
      setError('Failed to update the task. Your change was not applied. Please try again.');
    } finally {
      pendingTaskIds.current.delete(taskId);
      setSavingTaskIds(new Set(pendingTaskIds.current));
      finishSaving();
    }
  };

  // Function to get the appropriate field name based on department type
  const getAcknowledgedField = (departmentType) => {
    switch(departmentType) {
      case 'Mechanical': return 'MechanicalAcknowledged';
      case 'Electrical': return 'ElectricalAcknowledged';
      case 'Controls': return 'ControlsAcknowledged';
      case 'CSM': return 'CSMAcknowledged';
      default: return '';
    }
  };

  const getCompletedField = (departmentType) => {
    switch(departmentType) {
      case 'Mechanical': return 'MechanicalCompleted';
      case 'Electrical': return 'ElectricalCompleted';
      case 'Controls': return 'ControlsCompleted';
      case 'CSM': return 'CSMCompleted';
      default: return '';
    }
  };

  // Process change order tasks to find those assigned to current user or all tasks for admin
  const processChangeOrderTasks = useCallback((jobs, changeOrderDetails, changeOrderParent, generalProjects) => {
    if (!changeOrderDetails || !jobs || !changeOrderParent || !currentUser) {
      return;
    }

    const userTasks = [];
    const unassignedTasks = {
      Mechanical: [],
      Electrical: [],
      Controls: [],
      CSM: []
    };
    const isAdmin = roles.Admin;

    // Get user's department
    const isMechanical = userIsMechanical();
    const isElectrical = userIsElectrical();
    const isControls = userIsControls();

    // Expand change order details to include the appropriate fields
    if (changeOrderDetails && changeOrderDetails.length > 0) {
      changeOrderDetails.forEach(detail => {
        // For admin users, get all tasks that are assigned to anyone
        if (isAdmin) {          // Check mechanical tasks - handle both string "true" and boolean true
          if ((detail.ActionRequiredMechanical === "true" || detail.ActionRequiredMechanical === true)) {
            // Skip if both acknowledged and completed
            const mechAcknowledged = detail.MechanicalAcknowledged === "true" || detail.MechanicalAcknowledged === true;
            const mechCompleted = detail.MechanicalCompleted === "true" || detail.MechanicalCompleted === true;
            if (mechAcknowledged && mechCompleted) {
              // Skip this task as it's both acknowledged and completed
            } else {
              // Check if assigned to someone or unassigned
              if (detail.AssignedMechanical && detail.AssignedMechanical !== "0" && detail.AssignedMechanical !== 0) {
                userTasks.push({
                  ...detail,
                  departmentType: 'Mechanical',
                  assignedTo: detail.AssignedMechanical,
                  acknowledged: mechAcknowledged,
                  completed: mechCompleted
                });
              } else {
                // Look up project to find assigned engineer
                const project = generalProjects?.find(
                  p => idsMatch(getProjectId(p), getProjectId(detail))
                );
                const assignedEngineer = project?.MechanicalEngineer;
                
                if (assignedEngineer) {
                  userTasks.push({
                    ...detail,
                    departmentType: 'Mechanical',
                    assignedTo: assignedEngineer,
                    acknowledged: mechAcknowledged,
                    completed: mechCompleted
                  });
                } else {
                  // Add to unassigned mechanical tasks
                  unassignedTasks.Mechanical.push({
                    ...detail,
                    departmentType: 'Mechanical',
                    assignedTo: "Unassigned",
                    acknowledged: mechAcknowledged,
                    completed: mechCompleted
                  });
                }
              }
            }
          }          // Check electrical tasks
          if ((detail.ActionRequiredElectrical === "true" || detail.ActionRequiredElectrical === true)) {
            // Skip if both acknowledged and completed
            const elecAcknowledged = detail.ElectricalAcknowledged === "true" || detail.ElectricalAcknowledged === true;
            const elecCompleted = detail.ElectricalCompleted === "true" || detail.ElectricalCompleted === true;
            if (elecAcknowledged && elecCompleted) {
              // Skip this task as it's both acknowledged and completed
            } else {
              if (detail.AssignedElectrical && detail.AssignedElectrical !== "0" && detail.AssignedElectrical !== 0) {
                userTasks.push({
                  ...detail,
                  departmentType: 'Electrical',
                  assignedTo: detail.AssignedElectrical,
                  acknowledged: elecAcknowledged,
                  completed: elecCompleted
                });
              } else {
                // Look up project to find assigned engineer
                const project = generalProjects?.find(
                  p => idsMatch(getProjectId(p), getProjectId(detail))
                );
                const assignedEngineer = project?.ElectricalEngineer;
                
                if (assignedEngineer) {
                  userTasks.push({
                    ...detail,
                    departmentType: 'Electrical',
                    assignedTo: assignedEngineer,
                    acknowledged: elecAcknowledged,
                    completed: elecCompleted
                  });
                } else {
                  // Add to unassigned electrical tasks
                  unassignedTasks.Electrical.push({
                    ...detail,
                    departmentType: 'Electrical',
                    assignedTo: "Unassigned",
                    acknowledged: elecAcknowledged,
                    completed: elecCompleted
                  });
                }
              }
            }
          }

          // Check controls tasks
          if ((detail.ActionRequiredControls === "true" || detail.ActionRequiredControls === true)) {
            // Skip if both acknowledged and completed
            const controlsAcknowledged = detail.ControlsAcknowledged === "true" || detail.ControlsAcknowledged === true;
            const controlsCompleted = detail.ControlsCompleted === "true" || detail.ControlsCompleted === true;
            if (controlsAcknowledged && controlsCompleted) {
              // Skip this task as it's both acknowledged and completed
            } else {
              if (detail.AssignedControls && detail.AssignedControls !== "0" && detail.AssignedControls !== 0) {
                userTasks.push({
                  ...detail,
                  departmentType: 'Controls',
                  assignedTo: detail.AssignedControls,
                  acknowledged: controlsAcknowledged,
                  completed: controlsCompleted
                });
              } else {
                // Look up project to find assigned engineer
                const project = generalProjects?.find(
                  p => idsMatch(getProjectId(p), getProjectId(detail))
                );
                const assignedEngineer = project?.ControlsEngineer;
                
                if (assignedEngineer) {
                  userTasks.push({
                    ...detail,
                    departmentType: 'Controls',
                    assignedTo: assignedEngineer,
                    acknowledged: controlsAcknowledged,
                    completed: controlsCompleted
                  });
                } else {
                  // Add to unassigned controls tasks
                  unassignedTasks.Controls.push({
                    ...detail,
                    departmentType: 'Controls',
                    assignedTo: "Unassigned",
                    acknowledged: controlsAcknowledged,
                    completed: controlsCompleted
                  });
                }
              }
            }
          }

          // Check CSM tasks
          if (detail.ActionRequiredCSM === "true" || detail.ActionRequiredCSM === true) {
            // Skip if both acknowledged and completed
            const csmAcknowledged = detail.CSMAcknowledged === "true" || detail.CSMAcknowledged === true;
            const csmCompleted = detail.CSMCompleted === "true" || detail.CSMCompleted === true;
            if (csmAcknowledged && csmCompleted) {
              // Skip this task as it's both acknowledged and completed
            } else {              userTasks.push({
                ...detail,
                departmentType: 'CSM',
                assignedTo: "David Neesen",
                acknowledged: csmAcknowledged,
                completed: csmCompleted
              });
            }
          }
        } else {
          // For non-admin users, show their own tasks and unassigned tasks from their department
          
          // Check if task is assigned to current user as mechanical engineer
          if ((detail.ActionRequiredMechanical === "true" || detail.ActionRequiredMechanical === true)) {
            const mechAcknowledged = detail.MechanicalAcknowledged === "true" || detail.MechanicalAcknowledged === true;
            const mechCompleted = detail.MechanicalCompleted === "true" || detail.MechanicalCompleted === true;
            
            // Skip if both acknowledged and completed
            if (mechAcknowledged && mechCompleted) {
              // Skip this task
            } else {
              if (detail.AssignedMechanical === currentUser) {
                userTasks.push({
                  ...detail,
                  departmentType: 'Mechanical',
                  assignedTo: detail.AssignedMechanical,
                  acknowledged: mechAcknowledged,
                  completed: mechCompleted
                });
              } else if ((!detail.AssignedMechanical || detail.AssignedMechanical === "0" || detail.AssignedMechanical === 0) && isMechanical) {
                // Look up project to find assigned engineer
                const project = generalProjects?.find(
                  p => idsMatch(getProjectId(p), getProjectId(detail))
                );
                const assignedEngineer = project?.MechanicalEngineer;
                
                if (assignedEngineer === currentUser) {
                  userTasks.push({
                    ...detail,
                    departmentType: 'Mechanical',
                    assignedTo: assignedEngineer,
                    acknowledged: mechAcknowledged,
                    completed: mechCompleted
                  });
                } else {
                  // Add to unassigned mechanical tasks if user is in mechanical department
                  unassignedTasks.Mechanical.push({
                    ...detail,
                    departmentType: 'Mechanical',
                    assignedTo: "Unassigned",
                    acknowledged: mechAcknowledged,
                    completed: mechCompleted
                  });
                }
              }
            }
          }

          // Check if task is assigned to current user as electrical engineer
          if ((detail.ActionRequiredElectrical === "true" || detail.ActionRequiredElectrical === true)) {
            const elecAcknowledged = detail.ElectricalAcknowledged === "true" || detail.ElectricalAcknowledged === true;
            const elecCompleted = detail.ElectricalCompleted === "true" || detail.ElectricalCompleted === true;
            
            // Skip if both acknowledged and completed
            if (elecAcknowledged && elecCompleted) {
              // Skip this task
            } else {
              if (detail.AssignedElectrical === currentUser) {
                userTasks.push({
                  ...detail,
                  departmentType: 'Electrical',
                  assignedTo: detail.AssignedElectrical,
                  acknowledged: elecAcknowledged,
                  completed: elecCompleted
                });
              } else if ((!detail.AssignedElectrical || detail.AssignedElectrical === "0" || detail.AssignedElectrical === 0) && isElectrical) {
                // Look up project to find assigned engineer
                const project = generalProjects?.find(
                  p => idsMatch(getProjectId(p), getProjectId(detail))
                );
                const assignedEngineer = project?.ElectricalEngineer;
                
                if (assignedEngineer === currentUser) {
                  userTasks.push({
                    ...detail,
                    departmentType: 'Electrical',
                    assignedTo: assignedEngineer,
                    acknowledged: elecAcknowledged,
                    completed: elecCompleted
                  });
                } else {
                  // Add to unassigned electrical tasks if user is in electrical department
                  unassignedTasks.Electrical.push({
                    ...detail,
                    departmentType: 'Electrical',
                    assignedTo: "Unassigned",
                    acknowledged: elecAcknowledged,
                    completed: elecCompleted
                  });
                }
              }
            }
          }

          // Check if task is assigned to current user as controls engineer
          if ((detail.ActionRequiredControls === "true" || detail.ActionRequiredControls === true)) {
            const controlsAcknowledged = detail.ControlsAcknowledged === "true" || detail.ControlsAcknowledged === true;
            const controlsCompleted = detail.ControlsCompleted === "true" || detail.ControlsCompleted === true;
            
            // Skip if both acknowledged and completed
            if (controlsAcknowledged && controlsCompleted) {
              // Skip this task
            } else {
              if (detail.AssignedControls === currentUser) {
                userTasks.push({
                  ...detail,
                  departmentType: 'Controls',
                  assignedTo: detail.AssignedControls,
                  acknowledged: controlsAcknowledged,
                  completed: controlsCompleted
                });
              } else if ((!detail.AssignedControls || detail.AssignedControls === "0" || detail.AssignedControls === 0) && isControls) {
                // Look up project to find assigned engineer
                const project = generalProjects?.find(
                  p => idsMatch(getProjectId(p), getProjectId(detail))
                );
                const assignedEngineer = project?.ControlsEngineer;
                
                if (assignedEngineer === currentUser) {
                  userTasks.push({
                    ...detail,
                    departmentType: 'Controls',
                    assignedTo: assignedEngineer,
                    acknowledged: controlsAcknowledged,
                    completed: controlsCompleted
                  });
                } else {
                  // Add to unassigned controls tasks if user is in controls department
                  unassignedTasks.Controls.push({
                    ...detail,
                    departmentType: 'Controls',
                    assignedTo: "Unassigned",
                    acknowledged: controlsAcknowledged,
                    completed: controlsCompleted
                  });
                }
              }
            }
          }
        }
      });
    }
    
    // Add job details to all tasks
    const addJobDetailsToTasks = (taskList) => {
      return taskList.map(task => {
        const jobDetails = jobs.find(
          job => idsMatch(getProjectId(job), getProjectId(task))
        );
        // Find the parent change order for additional details
        const parentChangeOrder = changeOrderParent.find(co => 
          String(co.ChangeOrderNumber) === String(task.ChangeOrderNumber) &&
          idsMatch(co.ProjectID, getProjectId(task))
        );
        
        return {
          ...task,
          jobNumber: jobDetails?.JobNumber || 'Unknown',
          projectName: jobDetails?.ProjectName || 'Unknown',
          dateSubmitted: parentChangeOrder?.DateSubmitted || null,
          writtenBy: parentChangeOrder?.WrittenBy || 'Unknown'
        };
      });
    };

    // Sort tasks - not acknowledged first, then not completed, then by change order number
    const sortTasks = (taskList) => {
      return taskList.sort((a, b) => {
        // First sort by acknowledgement status
        if (!a.acknowledged && b.acknowledged) return -1;
        if (a.acknowledged && !b.acknowledged) return 1;
        
        // Then sort by completion status
        if (!a.completed && b.completed) return -1;
        if (a.completed && !b.completed) return 1;
        
        // Then sort by change order number (most recent first)
        return b.ChangeOrderNumber - a.ChangeOrderNumber;
      });
    };

    // Process all tasks
    if (isAdmin) {
      // Process assigned tasks
      const processedTasks = addJobDetailsToTasks(userTasks);
      sortTasks(processedTasks);
      
      // Process unassigned tasks by department
      const processedUnassignedTasks = {
        Mechanical: sortTasks(addJobDetailsToTasks(unassignedTasks.Mechanical)),
        Electrical: sortTasks(addJobDetailsToTasks(unassignedTasks.Electrical)),
        Controls: sortTasks(addJobDetailsToTasks(unassignedTasks.Controls)),
        CSM: sortTasks(addJobDetailsToTasks(unassignedTasks.CSM))
      };
      
      // Group tasks by assignee for admin view
      const groupedByUser = {};
      
      // Add assigned tasks
      processedTasks.forEach(task => {
        const assignee = task.assignedTo || 'Unassigned';
        if (!groupedByUser[assignee]) {
          groupedByUser[assignee] = [];
        }
        groupedByUser[assignee].push(task);
      });
      
      // Add unassigned departments section
      if (processedUnassignedTasks.Mechanical.length > 0) {
        groupedByUser['Unassigned - Mechanical'] = processedUnassignedTasks.Mechanical;
      }
      
      if (processedUnassignedTasks.Electrical.length > 0) {
        groupedByUser['Unassigned - Electrical'] = processedUnassignedTasks.Electrical;
      }
      
      if (processedUnassignedTasks.Controls.length > 0) {
        groupedByUser['Unassigned - Controls'] = processedUnassignedTasks.Controls;
      }
      
      setGroupedTasks(groupedByUser);
      setUnassignedByDept(processedUnassignedTasks);
    } else {
      // For regular users, combine their assigned tasks with unassigned tasks from their department
      let combinedTasks = [...userTasks];
      
      // Add unassigned tasks from user's department
      if (isMechanical) {
        combinedTasks = [...combinedTasks, ...unassignedTasks.Mechanical];
      }
      
      if (isElectrical) {
        combinedTasks = [...combinedTasks, ...unassignedTasks.Electrical];
      }
      
      if (isControls) {
        combinedTasks = [...combinedTasks, ...unassignedTasks.Controls];
      }
      
      // Add job details and sort
      const processedTasks = sortTasks(addJobDetailsToTasks(combinedTasks));
      
      setTasks(processedTasks);
      
      // Also set unassigned by dept for display in a separate section
      const processedUnassignedTasks = {
        Mechanical: isMechanical ? sortTasks(addJobDetailsToTasks(unassignedTasks.Mechanical)) : [],
        Electrical: isElectrical ? sortTasks(addJobDetailsToTasks(unassignedTasks.Electrical)) : [],
        Controls: isControls ? sortTasks(addJobDetailsToTasks(unassignedTasks.Controls)) : [],
        CSM: []
      };
        setUnassignedByDept(processedUnassignedTasks);
    }
  }, [currentUser, roles, userIsMechanical, userIsElectrical, userIsControls]);  // Process data when props change
  useEffect(() => {
    if (!loading) {
      processChangeOrderTasks(jobsData, changeOrderDetailsData, changeOrderParentData, generalProjectData);
    }
  }, [loading, jobsData, changeOrderDetailsData, changeOrderParentData, generalProjectData, processChangeOrderTasks]);
  const formatDate = (dateString) => {
    return formatDateLocalEnhanced(dateString);
  };

  const handleNavigateToTask = (projectId) => {
    dispatch(setJobId(projectId));
    navigate(`/${projectId}/changeOrders?tab=tasks`);
  };

  const getStatusBadge = (acknowledged, completed) => {
    if (!acknowledged) {
      return <Badge bg="danger">Unacknowledged</Badge>;
    } else if (!completed) {
      return <Badge bg="warning" text="dark">In Progress</Badge>;
    } else {
      return <Badge bg="success">Completed</Badge>;
    }
  };
  
  // Function to count unacknowledged tasks for a user
  const countUnacknowledgedTasks = (tasks) => {
    return tasks.filter(task => !task.acknowledged).length;
  };
  
  // Function to count in-progress tasks for a user
  const countInProgressTasks = (tasks) => {
    return tasks.filter(task => task.acknowledged && !task.completed).length;
  };

  // Check if there are any unassigned tasks
  const hasUnassignedTasks = () => {
    return Object.values(unassignedByDept).some(dept => dept.length > 0);
  };

  return (
    <div className="m-4">
      {error && <Alert variant="danger">{error}</Alert>}
      <EngineeringPortalNoticeBanner />
      <div className="mb-3 text-center"><h1>Open Change Order Tasks</h1></div>
      
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {/* Admin View - Grouped by user with accordions */}
          {roles.Admin && (
            <>
              {Object.keys(groupedTasks).length === 0 ? (
                <Alert variant="info">No pending change order tasks found.</Alert>
              ) : (
                <Accordion activeKey={activeAccordionKey} onSelect={key => setAccordionSelection({ user: currentUser, key })} className="mb-4">
                  {/* First display unassigned tasks by department */}
                  {Object.entries(unassignedByDept).map(([dept, deptTasks]) => (
                    deptTasks.length > 0 && (
                      <Accordion.Item eventKey={`unassigned:${dept}`} key={`unassigned-${dept}`}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 me-3">
                            <strong>Unassigned - {dept}</strong>
                            <div>
                              <Badge bg="danger" className="me-2">
                                {countUnacknowledgedTasks(deptTasks)} Unacknowledged
                              </Badge>
                              <Badge bg="warning" text="dark">
                                {countInProgressTasks(deptTasks)} In Progress
                              </Badge>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Table striped bordered hover responsive>
                            <thead>
                              <tr>
                                <th>Change Order #</th>
                                <th>Job</th>
                                <th>Task</th>
                                <th>Department</th>
                                <th>Change</th>
                                <th>Status</th>
                                <th>Date Submitted</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deptTasks.map((task) => (
                                <tr 
                                  key={`${task.ID}-${task.departmentType}`} 
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.ChangeOrderNumber}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                    {task.jobNumber} - {task.projectName}
                                  </td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.Title || 'Untitled Task'}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.departmentType}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                    <small>
                                      From: <strong>{task.ExistingValue || 'BLANK'}</strong><br />
                                      To: <strong>{task.NewValue || 'BLANK'}</strong>
                                    </small>
                                  </td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{getStatusBadge(task.acknowledged, task.completed)}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{formatDate(task.dateSubmitted)}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <div className="d-flex flex-column gap-1">
                                      <Form.Check
                                        type="checkbox"
                                        label="Ack"
                                        id={`ack-${task.ID}-${task.departmentType}`}
                                        checked={task.acknowledged}
                                        onChange={(e) => handleCheckboxChange(task, getAcknowledgedField(task.departmentType), e.target.checked)}
                                        disabled={!canModifyTask(task)}
                                      />
                                      <Form.Check
                                        type="checkbox"
                                        label="Done"
                                        id={`done-${task.ID}-${task.departmentType}`}
                                        checked={task.completed}
                                        disabled={!task.acknowledged || !canModifyTask(task)}
                                        onChange={(e) => handleCheckboxChange(task, getCompletedField(task.departmentType), e.target.checked)}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Accordion.Body>
                      </Accordion.Item>
                    )
                  ))}

                  {/* Then display tasks assigned to specific users */}
                  {Object.entries(groupedTasks)
                    .filter(([user, _]) => !user.startsWith('Unassigned -'))
                    .map(([user, userTasks]) => (
                      <Accordion.Item eventKey={`user:${user}`} key={user}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 me-3">
                            <strong>{user}</strong>
                            <div>
                              <Badge bg="danger" className="me-2">
                                {countUnacknowledgedTasks(userTasks)} Unacknowledged
                              </Badge>
                              <Badge bg="warning" text="dark">
                                {countInProgressTasks(userTasks)} In Progress
                              </Badge>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Table striped bordered hover responsive>
                            <thead>
                              <tr>
                                <th>Change Order #</th>
                                <th>Job</th>
                                <th>Task</th>
                                <th>Department</th>
                                <th>Change</th>
                                <th>Status</th>
                                <th>Date Submitted</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userTasks.map((task) => (
                                <tr 
                                  key={`${task.ID}-${task.departmentType}`} 
                                  style={{ cursor: 'pointer' }}
                                >
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.ChangeOrderNumber}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                    {task.jobNumber} - {task.projectName}
                                  </td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.Title || 'Untitled Task'}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.departmentType}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                    <small>
                                      From: <strong>{task.ExistingValue || 'BLANK'}</strong><br />
                                      To: <strong>{task.NewValue || 'BLANK'}</strong>
                                    </small>
                                  </td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{getStatusBadge(task.acknowledged, task.completed)}</td>
                                  <td onClick={() => handleNavigateToTask(task.ProjectID)}>{formatDate(task.dateSubmitted)}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <div className="d-flex flex-column gap-1">
                                      <Form.Check
                                        type="checkbox"
                                        label="Ack"
                                        id={`ack-${task.ID}-${task.departmentType}`}
                                        checked={task.acknowledged}
                                        onChange={(e) => handleCheckboxChange(task, getAcknowledgedField(task.departmentType), e.target.checked)}
                                        disabled={!canModifyTask(task)}
                                      />
                                      <Form.Check
                                        type="checkbox"
                                        label="Done"
                                        id={`done-${task.ID}-${task.departmentType}`}
                                        checked={task.completed}
                                        disabled={!task.acknowledged || !canModifyTask(task)}
                                        onChange={(e) => handleCheckboxChange(task, getCompletedField(task.departmentType), e.target.checked)}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                </Accordion>
              )}
            </>
          )}
          
          {/* Engineer View - First show their assigned tasks, then unassigned in their department */}
          {!roles.Admin && (
            <>
              {tasks.length === 0 && !hasUnassignedTasks() ? (
                <Alert variant="info">No pending change order tasks found.</Alert>
              ) : (
                <>
                  {/* Display assigned tasks */}
                  {tasks.filter(task => task.assignedTo !== "Unassigned").length > 0 && (
                    <>
                      <h4 className="mt-4 mb-3">Your Assigned Tasks</h4>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Change Order #</th>
                            <th>Job</th>
                            <th>Task</th>
                            <th>Change</th>
                            <th>Status</th>
                            <th>Date Submitted</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tasks.filter(task => task.assignedTo !== "Unassigned").map((task) => (
                            <tr 
                              key={`${task.ID}-${task.departmentType}`} 
                              style={{ cursor: 'pointer' }}
                            >
                              <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.ChangeOrderNumber}</td>
                              <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                {task.jobNumber} - {task.projectName}
                              </td>
                              <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.Title || 'Untitled Task'}</td>
                              <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                <small>
                                  From: <strong>{task.ExistingValue || 'BLANK'}</strong><br />
                                  To: <strong>{task.NewValue || 'BLANK'}</strong>
                                </small>
                              </td>
                              <td onClick={() => handleNavigateToTask(task.ProjectID)}>{getStatusBadge(task.acknowledged, task.completed)}</td>
                              <td onClick={() => handleNavigateToTask(task.ProjectID)}>{formatDate(task.dateSubmitted)}</td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className="d-flex flex-column gap-1">
                                  <Form.Check
                                    type="checkbox"
                                    label="Acknowledged"
                                    id={`ack-${task.ID}-${task.departmentType}`}
                                    checked={task.acknowledged}
                                    onChange={(e) => handleCheckboxChange(task, getAcknowledgedField(task.departmentType), e.target.checked)}
                                    disabled={!canModifyTask(task)}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    label="Completed"
                                    id={`done-${task.ID}-${task.departmentType}`}
                                    checked={task.completed}
                                    disabled={!task.acknowledged || !canModifyTask(task)}
                                    onChange={(e) => handleCheckboxChange(task, getCompletedField(task.departmentType), e.target.checked)}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}

                  {/* Display unassigned tasks by department */}
                  {Object.entries(unassignedByDept).map(([dept, deptTasks]) => (
                    deptTasks.length > 0 && (
                      <div key={`unassigned-${dept}`} className="mt-4">
                        <h4 className="mb-3">Unassigned {dept} Tasks</h4>
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>Change Order #</th>
                              <th>Job</th>
                              <th>Task</th>
                              <th>Change</th>
                              <th>Status</th>
                              <th>Date Submitted</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deptTasks.map((task) => (
                              <tr 
                                key={`${task.ID}-${task.departmentType}`} 
                                style={{ cursor: 'pointer' }}
                              >
                                <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.ChangeOrderNumber}</td>
                                <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                  {task.jobNumber} - {task.projectName}
                                </td>
                                <td onClick={() => handleNavigateToTask(task.ProjectID)}>{task.Title || 'Untitled Task'}</td>
                                <td onClick={() => handleNavigateToTask(task.ProjectID)}>
                                  <small>
                                    From: <strong>{task.ExistingValue || 'BLANK'}</strong><br />
                                    To: <strong>{task.NewValue || 'BLANK'}</strong>
                                  </small>
                                </td>
                                <td onClick={() => handleNavigateToTask(task.ProjectID)}>{getStatusBadge(task.acknowledged, task.completed)}</td>
                                <td onClick={() => handleNavigateToTask(task.ProjectID)}>{formatDate(task.dateSubmitted)}</td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <div className="d-flex flex-column gap-1">
                                    <Form.Check
                                      type="checkbox"
                                      label="Acknowledged"
                                      id={`ack-${task.ID}-${task.departmentType}`}
                                      checked={task.acknowledged}
                                      onChange={(e) => handleCheckboxChange(task, getAcknowledgedField(task.departmentType), e.target.checked)}
                                      disabled={!canModifyTask(task)}
                                    />
                                    <Form.Check
                                      type="checkbox"
                                      label="Completed"
                                      id={`done-${task.ID}-${task.departmentType}`}
                                      checked={task.completed}
                                      disabled={!task.acknowledged || !canModifyTask(task)}
                                      onChange={(e) => handleCheckboxChange(task, getCompletedField(task.departmentType), e.target.checked)}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )
                  ))}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default OpenChangeOrderTasks;
